import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

export type ButtonType = 'primary' | 'secondary' | 'warning';
export type ButtonSize = 'small' | 'medium' | 'large';

/**
 * The button. Emits `venus-btn` classes; the look is the design system's, not ours.
 *
 *   <app-button type="secondary" size="small" [isLoading]="saving" (onClick)="save()">Save</app-button>
 *
 * Inputs keep juno's original names so no call site moved. Mapping:
 *   type   primary → venus-btn. secondary → --outlined (white, primary border: the Cancel button).
 *          warning → --destructive (every caller is a void, cancel, or delete).
 *   size   small and medium → --sm, 32px, the internal default in ux_system_design/docs/internal-web.md.
 *          juno's 24px small sat below the system minimum. large → the 40px default. nothing maps to --lg.
 */
@Component({
  selector: 'app-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button [type]="htmlType()" [class]="classes" [disabled]="disabled() || isLoading()" (click)="handleClick($event)">
      <ng-content />
    </button>
  `,
  styles: `
    :host { display: inline-flex; }
    button { width: 100%; }
  `,
})
export class ButtonComponent {
  readonly type = input<ButtonType>('primary');
  readonly size = input<ButtonSize>('medium');
  readonly disabled = input(false);
  readonly isLoading = input(false);
  /** 'submit' inside a <form> keeps Enter-to-submit working. */
  readonly htmlType = input<'button' | 'submit'>('button');
  readonly onClick = output<void>();

  get classes(): string {
    const variant = { primary: '', secondary: 'venus-btn--outlined', warning: 'venus-btn--destructive' }[this.type()];
    const size = this.size() === 'large' ? '' : 'venus-btn--sm';
    return ['venus-btn', variant, size, this.isLoading() ? 'venus-btn--loading' : ''].filter(Boolean).join(' ');
  }

  handleClick(event: Event): void {
    event.stopPropagation();
    if (this.disabled() || this.isLoading()) return;
    this.onClick.emit();
  }
}
